/**
 * Observations Form Component
 * Similar to ItinerarySubmissions - allows adding multiple vehicle/individual observations with map
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Snackbar,
  Alert,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Route as RouteIcon,
  Clear as ClearIcon,
  MyLocation as MyLocationIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { format } from 'date-fns';
import * as observationsApi from '../../services/observationsApi';
import { useAuth } from '../../hooks/useAuth';
import { useParticipantLabels } from '../../hooks/useParticipantLabels';

interface ObservationsFormProps {
  vehicleObservations: observationsApi.VehicleObservation[];
  individualObservations: observationsApi.IndividualObservation[];
  selectedLocation: { lat: number; lon: number; address?: string } | null;
  onEnterMapMode: (type: 'vehicle' | 'individual', index: number) => void;
  onExitMapMode: () => void;
  onObservationsChange?: (vehicles: observationsApi.VehicleObservation[], individuals: observationsApi.IndividualObservation[]) => void;
  editData?: observationsApi.Observation;
  onEditComplete?: () => void;
  // Route data loading
  onLoadRouteData?: (startDate: Date, endDate: Date, studentNumber: string) => void;
  routeLoading?: boolean;
  routeDataLoaded?: boolean;
  onClearRouteData?: () => void;
}

// Choice lists from CSV
const STUDENT_NUMBERS = [
  ...Array.from({ length: 12 }, (_, i) => `Trainee-${String(i + 1).padStart(2, '0')}`),
  ...Array.from({ length: 64 }, (_, i) => `Student-${String(i + 1).padStart(2, '0')}`),
];

const DETACHMENTS = Array.from({ length: 8 }, (_, i) => `Detachment-${i + 1}`);

const VEHICLE_MAKES = [
  'Toyota', 'Honda', 'Ford', 'Chevrolet', 'Nissan', 'BMW', 'Mercedes-Benz', 'Audi',
  'Volkswagen', 'Hyundai', 'Kia', 'Subaru', 'Mazda', 'Jeep', 'Tesla', 'Lexus',
  'Dodge', 'Ram', 'GMC', 'Other'
];

const VEHICLE_COLORS = [
  'Black', 'White', 'Silver', 'Gray', 'Blue', 'Red', 'Green', 'Yellow',
  'Brown', 'Gold', 'Orange', 'Maroon', 'Beige', 'Teal', 'Navy', 'Burgundy', 'Other'
];

const GENDERS = ['Female', 'Male', 'Unknown'];

const RACES = [
  'White (Caucasion)', 'Black or African American', 'Asian', 
  'Native American or Alaska Native', 'Native Hawaiian or Other Pacific Islander',
  'Hispanic or Latino', 'Biracial or Multiracial', 'Middle Eastern or North African', 'Other'
];

const AGE_GROUPS = [
  '15-19', '20-24', '25-29', '30-34', '35-39', '40-44', '45-49',
  '50-54', '55-59', '60-64', '65-69', '70-74', '75+'
];

const BODY_BUILDS = [
  'Athletic', 'Slim', 'Average', 'Muscular', 'Stocky', 'Heavy', 'Who am I to judge?'
];

type FieldErrors = {
  observationDate?: string;
  studentNumber?: string;
  detachment?: string;
  vehiclesMissingLocation?: number[];
  individualsMissingLocation?: number[];
};

const ObservationsForm: React.FC<ObservationsFormProps> = ({
  vehicleObservations: _vehicleObservations,
  individualObservations: _individualObservations,
  selectedLocation,
  onEnterMapMode,
  onExitMapMode,
  onObservationsChange,
  editData,
  onEditComplete,
  onLoadRouteData,
  routeLoading = false,
  routeDataLoaded = false,
  onClearRouteData,
}) => {
  const { singular } = useParticipantLabels();
  // Auth context
  const { user } = useAuth();
  const isStudent = user?.roles?.includes('student');
  const usernameLower = user?.username?.toLowerCase() || '';
  const isTrainee = usernameLower.includes('trainee');
  const isStudentInUsername = usernameLower.includes('student');
  const isStudentOrTrainee = isStudent || isTrainee || isStudentInUsername;
  
  // Track if we've populated the form to prevent re-population on every render
  const populatedIdRef = useRef<number | null>(null);
  
  // Helper function to extract student number from username
  // Handles both "Student-X" and "Trainee-X" formats
  const extractStudentNumber = (username: string): string | null => {
    if (!username) return null;
    
    // Try to match Student-X or Trainee-X patterns
    const studentMatch = username.match(/Student[_-]?(\d+)/i);
    const traineeMatch = username.match(/Trainee[_-]?(\d+)/i);
    
    if (traineeMatch) {
      return `Trainee-${traineeMatch[1].padStart(2, '0')}`;
    }
    if (studentMatch) {
      return `Student-${studentMatch[1].padStart(2, '0')}`;
    }
    
    return null;
  };
  
  // Auto-select student number from username for students/trainees
  const prefixedStudentNumber = isStudentOrTrainee && user?.username ? 
    extractStudentNumber(user.username) || '' 
    : '';
  
  // Form state
  const [observationDate, setObservationDate] = useState(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
  const [studentNumber, setStudentNumber] = useState(prefixedStudentNumber || '');
  const [detachment, setDetachment] = useState('');
  const [areaObservances, setAreaObservances] = useState('');
  const [narrativeDescription, setNarrativeDescription] = useState('');
  
  // Vehicle observations state
  const [vehicles, setVehicles] = useState<observationsApi.VehicleObservation[]>([{
    vehicle_number: 1,
    vehicle_make: '',
    vehicle_model: '',
    vehicle_color: '',
    distinguishing_features: '',
    latitude: 0,
    longitude: 0,
    location_name_address: '',
    observation_datetime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
  }]);
  const [currentVehicleIndex, setCurrentVehicleIndex] = useState(0);
  
  // Individual observations state
  const [individuals, setIndividuals] = useState<observationsApi.IndividualObservation[]>([{
    individual_number: 1,
    gender: '',
    race: '',
    age_group: '',
    body_build: '',
    characteristics: '',
    latitude: 0,
    longitude: 0,
    location_name_address: '',
    observation_datetime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
  }]);
  const [currentIndividualIndex, setCurrentIndividualIndex] = useState(0);
  
  // UI state
  const [isLocationSelectionMode, setIsLocationSelectionMode] = useState<'vehicle' | 'individual' | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [showValidation, setShowValidation] = useState(false);
  const basicInfoRef = useRef<HTMLDivElement>(null);
  const vehicleSectionRef = useRef<HTMLDivElement>(null);
  const individualSectionRef = useRef<HTMLDivElement>(null);
  
  // Route loader state - start at observation time, end 12 hours after
  const getInitialRouteEnd = () => {
    const obsDate = new Date(observationDate);
    obsDate.setHours(obsDate.getHours() + 12);
    return format(obsDate, "yyyy-MM-dd'T'HH:mm");
  };
  const [routeStartTime, setRouteStartTime] = useState(observationDate);
  const [routeEndTime, setRouteEndTime] = useState(getInitialRouteEnd());
  
  // Sync route times when observation date changes
  // Start time matches observation, end time is 12 hours after (max allowed window)
  useEffect(() => {
    if (observationDate) {
      const obsDate = new Date(observationDate);
      const endDate = new Date(obsDate);
      endDate.setHours(endDate.getHours() + 12);
      setRouteStartTime(observationDate);
      setRouteEndTime(format(endDate, "yyyy-MM-dd'T'HH:mm"));
    }
  }, [observationDate]);
  
  // Notify parent of observations changes for map display
  useEffect(() => {
    if (onObservationsChange) {
      onObservationsChange(vehicles, individuals);
    }
  }, [vehicles, individuals, onObservationsChange]);
  
  // Auto-populate location when map is clicked (do not depend on vehicles/individuals — that re-ran the effect on every field change and could re-apply a stale selectedLocation)
  useEffect(() => {
    if (!selectedLocation || !isLocationSelectionMode) return;
    const { lat, lon, address } = selectedLocation;
    if (isLocationSelectionMode === 'vehicle') {
      setVehicles((prev) => {
        const next = [...prev];
        const i = currentVehicleIndex;
        next[i] = {
          ...next[i],
          latitude: lat,
          longitude: lon,
          location_name_address: address || '',
        };
        return next;
      });
    } else if (isLocationSelectionMode === 'individual') {
      setIndividuals((prev) => {
        const next = [...prev];
        const i = currentIndividualIndex;
        next[i] = {
          ...next[i],
          latitude: lat,
          longitude: lon,
          location_name_address: address || '',
        };
        return next;
      });
    }
    setIsLocationSelectionMode(null);
    onExitMapMode();
    if (isLocationSelectionMode === 'vehicle') {
      setFieldErrors((prev) => {
        const vehicleNum = currentVehicleIndex + 1;
        if (!prev.vehiclesMissingLocation?.includes(vehicleNum)) return prev;
        const remaining = prev.vehiclesMissingLocation.filter((n) => n !== vehicleNum);
        return {
          ...prev,
          vehiclesMissingLocation: remaining.length ? remaining : undefined,
        };
      });
    } else if (isLocationSelectionMode === 'individual') {
      setFieldErrors((prev) => {
        const individualNum = currentIndividualIndex + 1;
        if (!prev.individualsMissingLocation?.includes(individualNum)) return prev;
        const remaining = prev.individualsMissingLocation.filter((n) => n !== individualNum);
        return {
          ...prev,
          individualsMissingLocation: remaining.length ? remaining : undefined,
        };
      });
    }
  }, [selectedLocation, isLocationSelectionMode, currentVehicleIndex, currentIndividualIndex, onExitMapMode]);

  const scrollToRef = (ref: React.RefObject<HTMLDivElement | null>) => {
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const validateForm = useCallback((): { valid: boolean; errors: FieldErrors; messages: string[] } => {
    const errors: FieldErrors = {};
    const messages: string[] = [];

    if (!observationDate) {
      errors.observationDate = 'Route start date and time is required';
      messages.push('Route Start Date & Time');
    }
    if (!studentNumber) {
      errors.studentNumber = `${singular} number is required`;
      messages.push(`${singular} Number`);
    }
    if (!detachment) {
      errors.detachment = 'Detachment is required';
      messages.push('Detachment');
    }

    const vehiclesMissingLocation = vehicles
      .map((v, i) => (v.latitude === 0 && v.longitude === 0 ? i + 1 : null))
      .filter((n): n is number => n !== null);
    if (vehiclesMissingLocation.length > 0) {
      errors.vehiclesMissingLocation = vehiclesMissingLocation;
      messages.push(
        `Vehicle map location${vehiclesMissingLocation.length > 1 ? 's' : ''} (${vehiclesMissingLocation.join(', ')})`
      );
    }

    const individualsMissingLocation = individuals
      .map((v, i) => (v.latitude === 0 && v.longitude === 0 ? i + 1 : null))
      .filter((n): n is number => n !== null);
    if (individualsMissingLocation.length > 0) {
      errors.individualsMissingLocation = individualsMissingLocation;
      messages.push(
        `Individual map location${individualsMissingLocation.length > 1 ? 's' : ''} (${individualsMissingLocation.join(', ')})`
      );
    }

    return { valid: messages.length === 0, errors, messages };
  }, [observationDate, studentNumber, detachment, vehicles, individuals, singular]);

  const resetFormAfterSuccess = useCallback(() => {
    onExitMapMode();
    setIsLocationSelectionMode(null);
    setObservationDate(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    setStudentNumber(prefixedStudentNumber || '');
    setDetachment('');
    setAreaObservances('');
    setNarrativeDescription('');
    setVehicles([{
      vehicle_number: 1,
      vehicle_make: '',
      vehicle_model: '',
      vehicle_color: '',
      distinguishing_features: '',
      latitude: 0,
      longitude: 0,
      location_name_address: '',
      observation_datetime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    }]);
    setIndividuals([{
      individual_number: 1,
      gender: '',
      race: '',
      age_group: '',
      body_build: '',
      characteristics: '',
      latitude: 0,
      longitude: 0,
      location_name_address: '',
      observation_datetime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    }]);
    setCurrentVehicleIndex(0);
    setCurrentIndividualIndex(0);
    setFieldErrors({});
    setShowValidation(false);
  }, [onExitMapMode, prefixedStudentNumber]);
  
  // Populate form when editing existing observation
  useEffect(() => {
    if (editData && editData.id && populatedIdRef.current !== editData.id) {
      populatedIdRef.current = editData.id;
      
      // Populate main fields
      setObservationDate(format(new Date(editData.observation_date), "yyyy-MM-dd'T'HH:mm"));
      setStudentNumber(editData.student_number);
      setDetachment(editData.detachment);
      setAreaObservances(editData.area_observances || '');
      setNarrativeDescription(editData.narrative_description || '');
      
      // Populate vehicles
      if (editData.vehicle_observations && editData.vehicle_observations.length > 0) {
        setVehicles(editData.vehicle_observations.map(v => ({
          vehicle_number: v.vehicle_number,
          vehicle_make: v.vehicle_make || '',
          vehicle_model: v.vehicle_model || '',
          vehicle_color: v.vehicle_color || '',
          distinguishing_features: v.distinguishing_features || '',
          latitude: v.latitude,
          longitude: v.longitude,
          location_name_address: v.location_name_address || '',
          observation_datetime: v.observation_datetime || format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        })));
        setCurrentVehicleIndex(0);
      }
      
      // Populate individuals
      if (editData.individual_observations && editData.individual_observations.length > 0) {
        setIndividuals(editData.individual_observations.map(i => ({
          individual_number: i.individual_number,
          gender: i.gender || '',
          race: i.race || '',
          age_group: i.age_group || '',
          body_build: i.body_build || '',
          characteristics: i.characteristics || '',
          latitude: i.latitude,
          longitude: i.longitude,
          location_name_address: i.location_name_address || '',
          observation_datetime: i.observation_datetime || format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        })));
        setCurrentIndividualIndex(0);
      }
      
      // Notify parent about the observations for map display (only once on mount)
      if (onObservationsChange) {
        onObservationsChange(
          editData.vehicle_observations as observationsApi.VehicleObservation[],
          editData.individual_observations as observationsApi.IndividualObservation[]
        );
      }
    } else if (!editData) {
      // Reset the ref when editData is cleared
      populatedIdRef.current = null;
    }
  }, [editData, onObservationsChange]);

  
  // Handle vehicle field changes
  const handleVehicleChange = (field: keyof observationsApi.VehicleObservation, value: any) => {
    const updatedVehicles = [...vehicles];
    updatedVehicles[currentVehicleIndex] = {
      ...updatedVehicles[currentVehicleIndex],
      [field]: value,
    };
    setVehicles(updatedVehicles);
  };
  
  // Handle individual field changes
  const handleIndividualChange = (field: keyof observationsApi.IndividualObservation, value: any) => {
    const updatedIndividuals = [...individuals];
    updatedIndividuals[currentIndividualIndex] = {
      ...updatedIndividuals[currentIndividualIndex],
      [field]: value,
    };
    setIndividuals(updatedIndividuals);
  };
  
  // Add new vehicle
  const handleAddVehicle = () => {
    if (vehicles.length >= 6) {
      setSnackbar({ open: true, message: 'Maximum 6 vehicles allowed', severity: 'error' });
      return;
    }
    setVehicles([...vehicles, {
      vehicle_number: vehicles.length + 1,
      vehicle_make: '',
      vehicle_model: '',
      vehicle_color: '',
      distinguishing_features: '',
      latitude: 0,
      longitude: 0,
      location_name_address: '',
      observation_datetime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    }]);
    setCurrentVehicleIndex(vehicles.length);
  };
  
  // Delete vehicle
  const handleDeleteVehicle = () => {
    if (vehicles.length === 1) {
      setSnackbar({ open: true, message: 'At least one vehicle required', severity: 'error' });
      return;
    }
    const newVehicles = vehicles.filter((_, idx) => idx !== currentVehicleIndex);
    setVehicles(newVehicles);
    setCurrentVehicleIndex(Math.max(0, currentVehicleIndex - 1));
  };
  
  // Add new individual
  const handleAddIndividual = () => {
    if (individuals.length >= 6) {
      setSnackbar({ open: true, message: 'Maximum 6 individuals allowed', severity: 'error' });
      return;
    }
    setIndividuals([...individuals, {
      individual_number: individuals.length + 1,
      gender: '',
      race: '',
      age_group: '',
      body_build: '',
      characteristics: '',
      latitude: 0,
      longitude: 0,
      location_name_address: '',
      observation_datetime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    }]);
    setCurrentIndividualIndex(individuals.length);
  };
  
  // Delete individual
  const handleDeleteIndividual = () => {
    if (individuals.length === 1) {
      setSnackbar({ open: true, message: 'At least one individual required', severity: 'error' });
      return;
    }
    const newIndividuals = individuals.filter((_, idx) => idx !== currentIndividualIndex);
    setIndividuals(newIndividuals);
    setCurrentIndividualIndex(Math.max(0, currentIndividualIndex - 1));
  };
  
  // Submit observation
  const handleSubmit = async () => {
    const { valid, errors, messages } = validateForm();
    if (!valid) {
      setFieldErrors(errors);
      setShowValidation(true);

      if (errors.observationDate || errors.studentNumber || errors.detachment) {
        scrollToRef(basicInfoRef);
      } else if (errors.vehiclesMissingLocation?.length) {
        setCurrentVehicleIndex(errors.vehiclesMissingLocation[0] - 1);
        scrollToRef(vehicleSectionRef);
      } else if (errors.individualsMissingLocation?.length) {
        setCurrentIndividualIndex(errors.individualsMissingLocation[0] - 1);
        scrollToRef(individualSectionRef);
      }

      setSnackbar({
        open: true,
        message: `Please complete the highlighted fields: ${messages.join(', ')}`,
        severity: 'error',
      });
      return;
    }

    setFieldErrors({});
    setShowValidation(false);
    
    try {
      const observation: observationsApi.Observation = {
        observation_date: observationDate,
        student_number: studentNumber,
        detachment: detachment,
        area_observances: areaObservances,
        narrative_description: narrativeDescription,
        vehicle_observations: vehicles,
        individual_observations: individuals,
      };
      
      // Check if editing
      if (editData?.id) {
        await observationsApi.updateObservation(editData.id, observation);
        setSnackbar({ open: true, message: 'Observation updated successfully!', severity: 'success' });
        
        // Call completion callback and reset form
        setTimeout(() => {
          if (onEditComplete) {
            onEditComplete();
          }
          resetFormAfterSuccess();
        }, 1500);
      } else {
        await observationsApi.createObservation(observation);
        setSnackbar({ open: true, message: 'Observation submitted successfully!', severity: 'success' });
        
        // Reset in place (reload left parent/form map state stale so the next submission could reuse the last point)
        setTimeout(() => {
          resetFormAfterSuccess();
        }, 1500);
      }
    } catch (error: any) {
      console.error('Error submitting observation:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Failed to submit observation', 
        severity: 'error' 
      });
    }
  };
  
  const currentVehicle = vehicles[currentVehicleIndex];
  const currentIndividual = individuals[currentIndividualIndex];
  const currentVehicleLocationMissing =
    showValidation && fieldErrors.vehiclesMissingLocation?.includes(currentVehicleIndex + 1);
  const currentIndividualLocationMissing =
    showValidation && fieldErrors.individualsMissingLocation?.includes(currentIndividualIndex + 1);
  const vehicleSectionHasErrors = (fieldErrors.vehiclesMissingLocation?.length ?? 0) > 0;
  const individualSectionHasErrors = (fieldErrors.individualsMissingLocation?.length ?? 0) > 0;
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Submit Surveillance Observation
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Record vehicle and individual observations with location data
      </Typography>
      
      {/* Basic Information */}
      <Card
        ref={basicInfoRef}
        sx={{
          mb: 3,
          ...(showValidation && (fieldErrors.observationDate || fieldErrors.studentNumber || fieldErrors.detachment)
            ? { border: 2, borderColor: 'error.main' }
            : {}),
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom>Basic Information</Typography>
          <Stack spacing={2}>
            <TextField
              label="Route Start Date & Time"
              type="datetime-local"
              value={observationDate}
              onChange={(e) => {
                setObservationDate(e.target.value);
                if (e.target.value) {
                  setFieldErrors((prev) => ({ ...prev, observationDate: undefined }));
                }
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              error={showValidation && !!fieldErrors.observationDate}
              helperText={showValidation ? fieldErrors.observationDate : undefined}
            />
            <FormControl
              fullWidth
              required
              error={showValidation && !!fieldErrors.studentNumber}
            >
              <InputLabel>{`${singular} Number`}</InputLabel>
              <Select 
                value={studentNumber} 
                onChange={(e) => {
                  setStudentNumber(e.target.value);
                  if (e.target.value) {
                    setFieldErrors((prev) => ({ ...prev, studentNumber: undefined }));
                  }
                }}
                label={`${singular} Number`}
                disabled={isStudentOrTrainee}
              >
                {(isStudentOrTrainee 
                  ? STUDENT_NUMBERS.filter(num => num === prefixedStudentNumber)
                  : STUDENT_NUMBERS
                ).map(num => <MenuItem key={num} value={num}>{num}</MenuItem>)}
              </Select>
              {showValidation && fieldErrors.studentNumber && (
                <FormHelperText>{fieldErrors.studentNumber}</FormHelperText>
              )}
            </FormControl>
            <FormControl
              fullWidth
              required
              error={showValidation && !!fieldErrors.detachment}
            >
              <InputLabel>Detachment</InputLabel>
              <Select
                value={detachment}
                onChange={(e) => {
                  setDetachment(e.target.value);
                  if (e.target.value) {
                    setFieldErrors((prev) => ({ ...prev, detachment: undefined }));
                  }
                }}
                label="Detachment"
              >
                {DETACHMENTS.map(det => <MenuItem key={det} value={det}>{det}</MenuItem>)}
              </Select>
              {showValidation && fieldErrors.detachment && (
                <FormHelperText>{fieldErrors.detachment}</FormHelperText>
              )}
            </FormControl>
          </Stack>
        </CardContent>
      </Card>
      
      {/* Route Loader - Show My Route on Map */}
      {onLoadRouteData && studentNumber && (
        <Card sx={{ mb: 3, bgcolor: 'action.hover' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <RouteIcon color="primary" />
              <Typography variant="h6">Show My Route on Map</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Load your GPS track and dwell locations to help identify where observations occurred. Maximum 12 hour span.
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Start Time"
                  type="datetime-local"
                  value={routeStartTime}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    setRouteStartTime(newStart);
                    // If end time is more than 12 hours after start, adjust it
                    const startDate = new Date(newStart);
                    const endDate = new Date(routeEndTime);
                    const diffHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
                    if (diffHours > 12) {
                      const newEnd = new Date(startDate);
                      newEnd.setHours(newEnd.getHours() + 12);
                      setRouteEndTime(format(newEnd, "yyyy-MM-dd'T'HH:mm"));
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <TextField
                  label="End Time"
                  type="datetime-local"
                  value={routeEndTime}
                  onChange={(e) => {
                    const newEnd = e.target.value;
                    const startDate = new Date(routeStartTime);
                    const endDate = new Date(newEnd);
                    const diffHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
                    if (diffHours <= 12 && diffHours > 0) {
                      setRouteEndTime(newEnd);
                    } else if (diffHours > 12) {
                      setSnackbar({ open: true, message: 'Maximum time span is 12 hours', severity: 'error' });
                    } else {
                      setSnackbar({ open: true, message: 'End time must be after start time', severity: 'error' });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={routeLoading ? <CircularProgress size={16} color="inherit" /> : <RouteIcon />}
                  onClick={() => {
                    const start = new Date(routeStartTime);
                    const end = new Date(routeEndTime);
                    onLoadRouteData(start, end, studentNumber);
                  }}
                  disabled={routeLoading || !studentNumber}
                  sx={{ flex: 1 }}
                >
                  {routeLoading ? 'Loading...' : 'Load My Route'}
                </Button>
                {routeDataLoaded && onClearRouteData && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<ClearIcon />}
                    onClick={onClearRouteData}
                    disabled={routeLoading}
                  >
                    Clear
                  </Button>
                )}
              </Box>
              {routeDataLoaded && (
                <Chip
                  label="Route data loaded - visible on map"
                  color="success"
                  size="small"
                  icon={<RouteIcon />}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
      
      {/* Vehicle Observations */}
      <Card
        ref={vehicleSectionRef}
        sx={{
          mb: 3,
          ...(showValidation && vehicleSectionHasErrors ? { border: 2, borderColor: 'error.main' } : {}),
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Vehicle Observations</Typography>
            <Box>
              <IconButton onClick={() => setCurrentVehicleIndex(Math.max(0, currentVehicleIndex - 1))} disabled={currentVehicleIndex === 0}>
                <ChevronLeftIcon />
              </IconButton>
              <Chip
                label={`Vehicle ${currentVehicleIndex + 1} of ${vehicles.length}`}
                color={currentVehicleLocationMissing ? 'error' : 'default'}
              />
              <IconButton onClick={() => setCurrentVehicleIndex(Math.min(vehicles.length - 1, currentVehicleIndex + 1))} disabled={currentVehicleIndex === vehicles.length - 1}>
                <ChevronRightIcon />
              </IconButton>
              <IconButton onClick={handleAddVehicle} color="primary">
                <AddIcon />
              </IconButton>
              <IconButton onClick={handleDeleteVehicle} color="error" disabled={vehicles.length === 1}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
          
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Vehicle Make</InputLabel>
              <Select value={currentVehicle.vehicle_make || ''} onChange={(e) => handleVehicleChange('vehicle_make', e.target.value)} label="Vehicle Make">
                {VEHICLE_MAKES.map(make => <MenuItem key={make} value={make}>{make}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Vehicle Model"
              value={currentVehicle.vehicle_model || ''}
              onChange={(e) => handleVehicleChange('vehicle_model', e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Vehicle Color</InputLabel>
              <Select value={currentVehicle.vehicle_color || ''} onChange={(e) => handleVehicleChange('vehicle_color', e.target.value)} label="Vehicle Color">
                {VEHICLE_COLORS.map(color => <MenuItem key={color} value={color}>{color}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Distinguishing Features (License Plate, etc.)"
              value={currentVehicle.distinguishing_features || ''}
              onChange={(e) => handleVehicleChange('distinguishing_features', e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Observation Date & Time"
              type="datetime-local"
              value={currentVehicle.observation_datetime || ''}
              onChange={(e) => handleVehicleChange('observation_datetime', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Box sx={{
              border: 2,
              borderColor: currentVehicle.latitude !== 0 && currentVehicle.longitude !== 0
                ? 'success.main'
                : currentVehicleLocationMissing
                  ? 'error.main'
                  : 'warning.main',
              borderRadius: 1,
              p: 1.5,
              bgcolor: currentVehicle.latitude !== 0 && currentVehicle.longitude !== 0
                ? 'rgba(46, 125, 50, 0.08)'
                : currentVehicleLocationMissing
                  ? 'rgba(211, 47, 47, 0.08)'
                  : 'rgba(237, 108, 2, 0.08)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {currentVehicle.latitude !== 0 && currentVehicle.longitude !== 0 ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <WarningIcon color={currentVehicleLocationMissing ? 'error' : 'warning'} fontSize="small" />
                )}
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600 }}
                  color={currentVehicleLocationMissing ? 'error.main' : undefined}
                >
                  {currentVehicle.latitude !== 0 && currentVehicle.longitude !== 0 ? 'Location Set' : 'Location Required'}
                </Typography>
              </Box>

              <Button
                variant={currentVehicle.latitude !== 0 && currentVehicle.longitude !== 0 ? 'outlined' : 'contained'}
                startIcon={<MyLocationIcon />}
                onClick={() => {
                  setIsLocationSelectionMode('vehicle');
                  onEnterMapMode('vehicle', currentVehicleIndex);
                }}
                fullWidth
                sx={{ mb: 1 }}
              >
                {currentVehicle.latitude !== 0 && currentVehicle.longitude !== 0
                  ? 'Change Location on Map'
                  : 'Click on Map to Set Location'}
              </Button>

              {currentVehicle.latitude !== 0 && currentVehicle.longitude !== 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {currentVehicle.latitude.toFixed(6)}, {currentVehicle.longitude.toFixed(6)}
                  </Typography>
                  {currentVehicle.location_name_address && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {currentVehicle.location_name_address}
                    </Typography>
                  )}
                </Box>
              )}

              {currentVehicle.latitude === 0 && currentVehicle.longitude === 0 && (
                <Typography variant="caption" color={currentVehicleLocationMissing ? 'error.main' : 'warning.main'}>
                  {currentVehicleLocationMissing
                    ? 'Map location is required. Click the button above, then click a location on the map.'
                    : 'Click the button above, then click a location on the map'}
                </Typography>
              )}
            </Box>
          </Stack>
          {showValidation && vehicleSectionHasErrors && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
              Missing map location for vehicle{fieldErrors.vehiclesMissingLocation!.length > 1 ? 's' : ''}: {fieldErrors.vehiclesMissingLocation!.join(', ')}
            </Typography>
          )}
        </CardContent>
      </Card>
      
      {/* Individual Observations */}
      <Card
        ref={individualSectionRef}
        sx={{
          mb: 3,
          ...(showValidation && individualSectionHasErrors ? { border: 2, borderColor: 'error.main' } : {}),
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Individual Observations</Typography>
            <Box>
              <IconButton onClick={() => setCurrentIndividualIndex(Math.max(0, currentIndividualIndex - 1))} disabled={currentIndividualIndex === 0}>
                <ChevronLeftIcon />
              </IconButton>
              <Chip
                label={`Individual ${currentIndividualIndex + 1} of ${individuals.length}`}
                color={currentIndividualLocationMissing ? 'error' : 'default'}
              />
              <IconButton onClick={() => setCurrentIndividualIndex(Math.min(individuals.length - 1, currentIndividualIndex + 1))} disabled={currentIndividualIndex === individuals.length - 1}>
                <ChevronRightIcon />
              </IconButton>
              <IconButton onClick={handleAddIndividual} color="primary">
                <AddIcon />
              </IconButton>
              <IconButton onClick={handleDeleteIndividual} color="error" disabled={individuals.length === 1}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
          
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select value={currentIndividual.gender || ''} onChange={(e) => handleIndividualChange('gender', e.target.value)} label="Gender">
                {GENDERS.map(gender => <MenuItem key={gender} value={gender}>{gender}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Race</InputLabel>
              <Select value={currentIndividual.race || ''} onChange={(e) => handleIndividualChange('race', e.target.value)} label="Race">
                {RACES.map(race => <MenuItem key={race} value={race}>{race}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Age Group</InputLabel>
              <Select value={currentIndividual.age_group || ''} onChange={(e) => handleIndividualChange('age_group', e.target.value)} label="Age Group">
                {AGE_GROUPS.map(age => <MenuItem key={age} value={age}>{age}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Body Build</InputLabel>
              <Select value={currentIndividual.body_build || ''} onChange={(e) => handleIndividualChange('body_build', e.target.value)} label="Body Build">
                {BODY_BUILDS.map(build => <MenuItem key={build} value={build}>{build}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Characteristics"
              value={currentIndividual.characteristics || ''}
              onChange={(e) => handleIndividualChange('characteristics', e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Observation Date & Time"
              type="datetime-local"
              value={currentIndividual.observation_datetime || ''}
              onChange={(e) => handleIndividualChange('observation_datetime', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Box sx={{
              border: 2,
              borderColor: currentIndividual.latitude !== 0 && currentIndividual.longitude !== 0
                ? 'success.main'
                : currentIndividualLocationMissing
                  ? 'error.main'
                  : 'warning.main',
              borderRadius: 1,
              p: 1.5,
              bgcolor: currentIndividual.latitude !== 0 && currentIndividual.longitude !== 0
                ? 'rgba(46, 125, 50, 0.08)'
                : currentIndividualLocationMissing
                  ? 'rgba(211, 47, 47, 0.08)'
                  : 'rgba(237, 108, 2, 0.08)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {currentIndividual.latitude !== 0 && currentIndividual.longitude !== 0 ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <WarningIcon color={currentIndividualLocationMissing ? 'error' : 'warning'} fontSize="small" />
                )}
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600 }}
                  color={currentIndividualLocationMissing ? 'error.main' : undefined}
                >
                  {currentIndividual.latitude !== 0 && currentIndividual.longitude !== 0 ? 'Location Set' : 'Location Required'}
                </Typography>
              </Box>

              <Button
                variant={currentIndividual.latitude !== 0 && currentIndividual.longitude !== 0 ? 'outlined' : 'contained'}
                startIcon={<MyLocationIcon />}
                onClick={() => {
                  setIsLocationSelectionMode('individual');
                  onEnterMapMode('individual', currentIndividualIndex);
                }}
                fullWidth
                sx={{ mb: 1 }}
              >
                {currentIndividual.latitude !== 0 && currentIndividual.longitude !== 0
                  ? 'Change Location on Map'
                  : 'Click on Map to Set Location'}
              </Button>

              {currentIndividual.latitude !== 0 && currentIndividual.longitude !== 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {currentIndividual.latitude.toFixed(6)}, {currentIndividual.longitude.toFixed(6)}
                  </Typography>
                  {currentIndividual.location_name_address && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {currentIndividual.location_name_address}
                    </Typography>
                  )}
                </Box>
              )}

              {currentIndividual.latitude === 0 && currentIndividual.longitude === 0 && (
                <Typography variant="caption" color={currentIndividualLocationMissing ? 'error.main' : 'warning.main'}>
                  {currentIndividualLocationMissing
                    ? 'Map location is required. Click the button above, then click a location on the map.'
                    : 'Click the button above, then click a location on the map'}
                </Typography>
              )}
            </Box>
          </Stack>
          {showValidation && individualSectionHasErrors && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
              Missing map location for individual{fieldErrors.individualsMissingLocation!.length > 1 ? 's' : ''}: {fieldErrors.individualsMissingLocation!.join(', ')}
            </Typography>
          )}
        </CardContent>
      </Card>
      
      {/* Area & Narrative */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Additional Information</Typography>
          <Stack spacing={2}>
            <TextField
              label="Area of Observances (describe the general area)"
              value={areaObservances}
              onChange={(e) => setAreaObservances(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
            <TextField
              label="Narrative Description of Identification"
              value={narrativeDescription}
              onChange={(e) => setNarrativeDescription(e.target.value)}
              multiline
              rows={4}
              fullWidth
              placeholder="Briefly explain your observations..."
            />
          </Stack>
        </CardContent>
      </Card>
      
      {/* Submit Button */}
      <Button
        variant="contained"
        size="large"
        startIcon={<SaveIcon />}
        onClick={handleSubmit}
        fullWidth
        sx={{ py: 1.5 }}
      >
        {editData ? 'Update Observation' : 'Submit Observation'}
      </Button>
      
      {/* Snackbar - anchored top-center so errors aren't hidden behind the form panel */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 8000 : 4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 100, mt: 2 }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ boxShadow: 4, minWidth: 320, maxWidth: 560 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ObservationsForm;

